import { useEffect, useRef, useState } from 'react';
import { local, saveRecord, seedHomeProgram, starterExercises } from './db';
import type { Exercise, Measurement, WorkoutPlan, WorkoutSession } from './types';

export function useTraining() {
  const [sessions, setSessions] = useState<WorkoutSession[]>([]);
  const [plans, setPlans] = useState<WorkoutPlan[]>([]);
  const [measurements, setMeasurements] = useState<Measurement[]>([]);
  const [catalog, setCatalog] = useState<Exercise[]>(starterExercises);
  const [loading,setLoading] = useState(true);
  const [error,setError] = useState('');
  const [pending,setPending] = useState(0);
  const sessionsRef = useRef(sessions);
  const pipeline = useRef<Promise<void>>(Promise.resolve());
  const failed = useRef(new Map<string, () => Promise<void>>());
  const [unsaved,setUnsaved] = useState(0);
  const write = (key: string, operation: () => Promise<void>) => {
    setPending(n => n+1);
    const task = pipeline.current.then(operation);
    pipeline.current = task.catch(() => undefined);
    return task.then(() => { failed.current.delete(key); if (!failed.current.size) setError(''); })
      .catch(e => { failed.current.set(key,operation); setError(e instanceof Error ? e.message : 'Penyimpanan gagal.'); throw e; })
      .finally(() => {setPending(n => n-1);setUnsaved(failed.current.size);});
  };
  const retry = async () => { await Promise.allSettled([...failed.current].map(([key,operation]) => write(key,operation))); };
  useEffect(() => {
    const protect = (event: BeforeUnloadEvent) => {if(pending || unsaved){event.preventDefault();event.returnValue='';}};
    window.addEventListener('beforeunload',protect);
    return () => window.removeEventListener('beforeunload',protect);
  },[pending,unsaved]);
  const reload = async () => {
    const [s,p,m,e] = await Promise.all([local.sessions(),local.plans(),local.measurements(),local.customExercises()]);
    const sorted = s.sort((a,b) => b.startedAt.localeCompare(a.startedAt));
    sessionsRef.current = sorted; setSessions(sorted);
    setPlans(p.sort((a,b) => a.createdAt.localeCompare(b.createdAt)));
    setMeasurements(m.sort((a,b) => b.recordedAt.localeCompare(a.recordedAt)));
    setCatalog([...new Map([...starterExercises,...e].map(v => [v.id,v])).values()]);
  };
  useEffect(() => { void seedHomeProgram().then(reload).catch(e => setError(String(e))).finally(() => setLoading(false)); }, []);
  const saveSession = (session: WorkoutSession) => {
    const next = {...session, updatedAt:new Date().toISOString()};
    const list = [next,...sessionsRef.current.filter(s => s.id !== next.id)].sort((a,b) => b.startedAt.localeCompare(a.startedAt));
    const publish = () => {sessionsRef.current = list; setSessions(list);};
    if(next.status === 'active') publish();
    return write(`sessions:${next.id}`,async () => {
      await saveRecord('sessions',next,'session.upsert');
      if(next.status !== 'active') {
        const saved = [next,...sessionsRef.current.filter(s => s.id !== next.id)].sort((a,b) => b.startedAt.localeCompare(a.startedAt));
        sessionsRef.current=saved;setSessions(saved);
      }
    });
  };
  const mutateSession = (id: string, mutation: (session: WorkoutSession) => WorkoutSession) => {
    const current = sessionsRef.current.find(s => s.id === id);
    return current ? saveSession(mutation(current)) : Promise.reject(new Error('Sesi tidak ditemukan.'));
  };
  const savePlan = (plan: WorkoutPlan) => write(`plans:${plan.id}`,async () => {
    await saveRecord('plans',plan,'plan.upsert'); setPlans(list => [...list.filter(p => p.id !== plan.id), plan]);
  });
  const saveExercise = (exercise: Exercise) => write(`exercises:${exercise.id}`,async () => {
    await saveRecord('exercises',exercise,'exercise.upsert'); setCatalog(list => [...list.filter(e => e.id !== exercise.id),exercise]);
  });
  const saveMeasurement = (measurement: Measurement) => write(`measurements:${measurement.id}`,async () => {
    await saveRecord('measurements',measurement,'measurement.upsert');
    setMeasurements(list => [measurement,...list.filter(m => m.id !== measurement.id)].sort((a,b) => b.recordedAt.localeCompare(a.recordedAt)));
  });
  return { sessions,plans,measurements,catalog,loading,error,pending,unsaved,retry,reload,savePlan,saveExercise,saveMeasurement,saveSession,mutateSession,
    active: sessions.find(s => s.status === 'active') };
}
